import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import Autocomplete from '@mui/material/Autocomplete';
import { LoadingButton } from '@mui/lab';
import { orderGuideBulkSubmit, vendorPreferencesBulkSubmit, orderGuideBulkSubmitExcel } from '../../../../../api/order-guides';
import { getOrderGuides } from '../../../../../redux/slices/orderGuides';
import { useDispatch } from '../../../../../redux/store';
import OrderGuideExcelUpload from './OrderGuideExcelUpload';
import OrderGuideVendorPreferencesBulkLoad from './OrderGuideVendorPreferencesBulkLoad';
import OrderGuideItemsBulkUpload from './OrderGuideItemsBulkLoad';
import CloseIconButton from '../../../../../reusable-components/basic-buttons/CloseIconButton';
import { download } from '../../../../../utils/download';

ManageOrderGuides.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  groups: PropTypes.array,
  facilities: PropTypes.array,
  categories: PropTypes.array,
  vendors: PropTypes.array,
  subcategories: PropTypes.array,
  allProducts: PropTypes.array,
}

export default function ManageOrderGuides({ open, setOpen, groups, facilities, categories, vendors, subcategories, allProducts }) {
  const dispatch = useDispatch();

  const [mode, setMode] = useState("Add");
  const [updateType, setUpdateType] = useState(null);
  const [formData, setFormData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setFormData(null); }, [updateType]);

  const handleClose = () => {
    setOpen(false);    
    setMode("Add")
    setUpdateType(null);
    setSubmitting(false);
  };

  const handleSave = () => {
    setSubmitting(true);
    const data = { ...formData, mode };
    if (updateType === 'Order Guide Items') {
      orderGuideBulkSubmit(data)
        .then(res => {
          if (res.data) handleSaveSuccess('Order guide items saved successfully');
          else enqueueSnackbar('Order guide items failed to save', { variant: 'error' });
        }).catch(() => enqueueSnackbar('Order guide items failed to save', { variant: 'error' }));
    }
    else if (updateType === 'Vendor preferences' || updateType === 'Vendor restrictions') {
      vendorPreferencesBulkSubmit(data).then(res => {
        if (res.data) handleSaveSuccess('Vendor preferences saved successfully');
        else enqueueSnackbar('Vendor preferences failed to save', { variant: 'error' });
      }).catch(() => enqueueSnackbar('Vendor preferences failed to save', { variant: 'error' }));
    }
    else if (updateType === 'Order Guide Excel Upload') {
      orderGuideBulkSubmitExcel(data).then((res) => {
        download(res.data, "Order Guide Bulk Submit.xlsx");
        handleSaveSuccess('Order guide items saved successfully')
      }).catch(() => enqueueSnackbar('Order guide items failed to save', { variant: 'error' }));
    }
    dispatch(getOrderGuides());
  };

  const handleSaveSuccess = (message) => {
    enqueueSnackbar(message, { variant: 'success' });   
    handleClose();
  };

  return (
    <Dialog open={open} onClose={(event, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      handleClose();
    }} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', mt: 2 }}>
        Manage Order Guides
        <CloseIconButton onClick={handleClose} />
      </DialogTitle>

      <DialogContent sx={{ overflowX: 'hidden', overflowY: 'scroll', height: '100%', width: '100%' }}>
        <Autocomplete
          sx={{ my: 1 }}
          size="small"
          options={['Order Guide Items', 'Vendor preferences', 'Vendor restrictions', "Order Guide Excel Upload"]}
          renderInput={(params) => <TextField {...params} label="Manage..." autoComplete="off"/>}
          onChange={(e, inputValue) => setUpdateType(inputValue)}
        />
        <Typography>Mode</Typography>
        <Stack direction="row" spacing={1}>
          <Box><Checkbox sx={{ p: 0 }} checked={mode === 'Add'} onChange={() => setMode("Add")} /> Add</Box>
          <Box><Checkbox sx={{ p: 0 }} checked={mode === 'Remove'} onChange={() => setMode('Remove')} /> Remove</Box>
          <Box><Checkbox sx={{ p: 0 }} checked={mode === 'Replace'} onChange={() => setMode('Replace')} /> Replace all</Box>
        </Stack>
        {updateType === "Order Guide Items" && <OrderGuideItemsBulkUpload
          categories={categories}
          allProducts={allProducts}
          groups={groups}
          facilities={facilities}
          setFormData={setFormData}
        />}
        {updateType === "Vendor preferences" && <OrderGuideVendorPreferencesBulkLoad
          categories={categories}
          allSubcategories={subcategories?.map(value => ({ ...value, value: value.id, label: value.name }))}
          vendors={vendors}
          groups={groups}
          facilities={facilities}
          setFormData={setFormData}
        />}
        {updateType === "Vendor restrictions" && <OrderGuideVendorPreferencesBulkLoad
          categories={categories}
          allSubcategories={subcategories?.map(value => ({ ...value, value: value.id, label: value.name }))}
          vendors={vendors}
          groups={groups}
          facilities={facilities}
          setFormData={setFormData}
          isRestricted
        />}
        {updateType === "Order Guide Excel Upload" && <OrderGuideExcelUpload formData={formData} setFormData={setFormData} />}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
          Cancel
        </Button>
        
        <LoadingButton
          loading={submitting}
          variant="contained"
          color="primary"
          size="medium"
          sx={{ width: 150 }}
          type="submit"
          onClick={handleSave}
          disabled={(!(formData?.categoryId) && !(formData?.categories?.length) && !(formData?.categoryCol))}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
